import { ArrowRightIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScopeContext } from "./ScopeContext";
import ScopeMention from "./ScopeMention";

interface ComposerProps {
  prompt: string;
  setPrompt: (next: string) => void;
  onSubmit?: (prompt: string) => void;
  disabled?: boolean;
}

export const Composer = ({ prompt, setPrompt, onSubmit, disabled = true }: ComposerProps) => {
  const { t } = useTranslation();
  const { modelName, availableUnits, selectedUnitIds, toggleUnit } = useScopeContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const mentionStartRef = useRef<number | null>(null);

  const closeMention = () => {
    setMentionOpen(false);
    setMentionQuery("");
    mentionStartRef.current = null;
  };

  // Recompute the mention query when the prompt changes while the menu is open.
  useEffect(() => {
    if (!mentionOpen) return;
    const start = mentionStartRef.current;
    if (start === null) return;
    if (start > prompt.length || prompt[start] !== "@") {
      closeMention();
      return;
    }
    const caret = inputRef.current?.selectionStart ?? prompt.length;
    if (caret <= start) {
      closeMention();
      return;
    }
    setMentionQuery(prompt.slice(start + 1, caret));
  }, [prompt, mentionOpen]);

  const handleSubmit = () => {
    if (disabled) return;
    closeMention();
    onSubmit?.(prompt);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    const caret = e.target.selectionStart ?? next.length;
    // Detect a new `@` trigger inserted at the caret.
    const justTypedAt =
      next.length > prompt.length &&
      caret > 0 &&
      next[caret - 1] === "@" &&
      (caret === 1 || /\s/.test(next[caret - 2]));
    if (justTypedAt && availableUnits.length > 0) {
      mentionStartRef.current = caret - 1;
      setMentionQuery("");
      setMentionOpen(true);
    }
    setPrompt(next);
  };

  const handleSelectUnit = (unit: { id: string }) => {
    if (!selectedUnitIds.includes(unit.id)) toggleUnit(unit.id);
    // Strip the "@query" fragment from the prompt.
    const start = mentionStartRef.current;
    if (start !== null) {
      const caret = inputRef.current?.selectionStart ?? prompt.length;
      const next = prompt.slice(0, start) + prompt.slice(caret);
      setPrompt(next);
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(start, start);
      });
    }
    closeMention();
  };

  const availableMentionUnits = availableUnits.filter((u) => !selectedUnitIds.includes(u.id));

  return (
    <div className="relative flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs placeholder-gray-400 bg-white disabled:bg-gray-50"
        placeholder={t("general.assistantScenarIa.composerPlaceholder") as string}
        value={prompt}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (mentionOpen) {
            // Let ScopeMention handle navigation keys.
            if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) {
              return;
            }
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      {mentionOpen && (
        <ScopeMention
          units={availableMentionUnits}
          modelName={modelName}
          query={mentionQuery}
          onSelect={handleSelectUnit}
          onClose={closeMention}
        />
      )}
      <button
        type="button"
        data-rail-send
        className="p-2 rounded-md bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleSubmit}
        disabled={disabled}
        aria-label={t("general.assistantScenarIa.sendAriaLabel") as string}
      >
        <ArrowRightIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default Composer;
